/**
 * Action creators for the contacts feature.
 */
import { CONTACTS_LOADED, CONTACTS_LOADING, CONTACTS_LOAD_FAILED, CONTACT_ADDED, CONTACT_UPDATED, CONTACT_FAVORITE_CONFIRMED, CONTACT_DELETED, CONTACT_DETAIL_LOADED, CONTACT_DETAIL_LOADING, CONTACT_DETAIL_FAILED, CONTACT_DETAIL_CLOSED, CONTACT_NOTE_ADDED, CONTACT_NOTE_UPDATED, CONTACT_NOTE_DELETED, CONTACT_GROUPS_LOADED, CONTACT_GROUP_ADDED, CONTACT_GROUP_DELETED, CONTACT_BLOCKED_LOADED, CONTACTS_SYNC_RECEIVED, CONTACTS_SYNC_TOKEN_UPDATED, CONTACTS_SET_SEARCH, CONTACTS_SET_TAB, CONTACTS_SET_GROUP_FILTER, CONTACTS_ERROR, CONTACTS_CLEAR_ERROR } from './actionTypes';
import { contactsAPI } from './contactsAPI';
import { v4 as uuidv4 } from 'uuid';
import { contactDetailScope } from './contactDetailRecovery';
const sameDetailScope = (scope) => { try {
    return contactDetailScope() === scope;
}
catch {
    return false;
} };
/**
 * Load the contacts list.
 */
export function loadContacts(search, groupId, favoritesOnly) {
    return async (dispatch, getState) => {
        const requestId = uuidv4();
        dispatch({ type: CONTACTS_LOADING, requestId });
        let scope;
        try {
            scope = contactDetailScope();
            const state = getState()['features/contacts'];
            const effectiveSearch = search !== undefined ? search : state.search || undefined;
            const effectiveGroupId = groupId !== undefined ? groupId : state.selectedGroupId || undefined;
            const effectiveFavoritesOnly = favoritesOnly !== undefined ? favoritesOnly : state.activeTab === 'favorites';
            const result = await contactsAPI.list(effectiveSearch, effectiveGroupId, effectiveFavoritesOnly || undefined);
            if (!sameDetailScope(scope))
                throw new Error('Your account changed. Reopen Contacts.');
            dispatch({ type: CONTACTS_LOADED, requestId, contacts: result.contacts });
        }
        catch (error) {
            dispatch({ type: CONTACTS_LOAD_FAILED, requestId,
                error: scope && !sameDetailScope(scope) ? 'Your account changed. Reopen Contacts.'
                    : error instanceof Error ? error.message : 'Contacts could not be loaded. Try again.' });
        }
    };
}
/**
 * Load full contact detail with timeline and notes.
 */
export function loadContactDetail(contactId) {
    return async (dispatch, getState) => {
        const requestId = uuidv4(), current = getState()['features/contacts'];
        const viewId = current.selectedContactId === contactId && current.detailViewId ? current.detailViewId : uuidv4();
        dispatch({ type: CONTACT_DETAIL_LOADING, contactId, requestId, viewId });
        let scope;
        try {
            scope = contactDetailScope();
            const result = await contactsAPI.get(contactId);
            if (!sameDetailScope(scope)) {
                if (getState()['features/contacts'].detailRequestId === requestId)
                    dispatch(closeContactDetail());
                return false;
            }
            dispatch({ type: CONTACT_DETAIL_LOADED, contactId, requestId, scope, contactDetail: result });
            return getState()['features/contacts'].detailRequestId === requestId;
        }
        catch (error) {
            if (scope && !sameDetailScope(scope)) {
                if (getState()['features/contacts'].detailRequestId === requestId)
                    dispatch(closeContactDetail());
                return false;
            }
            dispatch({ type: CONTACT_DETAIL_FAILED, contactId, requestId,
                error: error instanceof Error ? error.message : 'Contact details could not be loaded. Try again.' });
            return false;
        }
    };
}
/** Show the complete owned record already checked by the contact editor. */
export function showSavedContactDetail(detail) {
    return (dispatch) => {
        const scope = contactDetailScope(), requestId = uuidv4(), viewId = uuidv4(), contactId = detail.contact.id;
        dispatch({ type: CONTACT_UPDATED, contact: detail.contact });
        dispatch({ type: CONTACT_DETAIL_LOADING, contactId, requestId, viewId });
        dispatch({ type: CONTACT_DETAIL_LOADED, contactId, requestId, scope, contactDetail: detail });
    };
}
/**
 * Close contact detail view.
 */
export function closeContactDetail() {
    return { type: CONTACT_DETAIL_CLOSED };
}
/**
 * Create a new contact.
 */
export function addContact(data) {
    return async (dispatch) => {
        try {
            const result = await contactsAPI.create(data);
            if (result.contact) {
                dispatch({ type: CONTACT_ADDED, contact: result.contact });
            }
            else {
                // Reload full list since we didn't get the complete contact back
                dispatch(loadContacts());
            }
        }
        catch (error) {
            dispatch({ type: CONTACTS_ERROR, error: error.message });
        }
    };
}
/**
 * Update an existing contact.
 */
export function updateContactAction(contactId, data) {
    return async (dispatch) => {
        try {
            await contactsAPI.update(contactId, data);
            // Reload to get the updated contact
            dispatch(loadContacts());
            if (data.groupIds) {
                dispatch(loadGroups());
            }
        }
        catch (error) {
            dispatch({ type: CONTACTS_ERROR, error: error.message });
        }
    };
}
/**
 * Delete a contact.
 */
export function deleteContactAction(contactId) {
    return async (dispatch, getState) => {
        const scope = contactDetailScope(), state = getState()['features/contacts'];
        if (state.selectedContactId !== contactId || state.contactDetail?.contact.id !== contactId
            || state.isDetailLoading || state.detailError || state.detailScope !== scope) {
            throw new Error('Refresh this contact before deleting it.');
        }
        await contactsAPI.delete(contactId);
        if (!sameDetailScope(scope))
            throw new Error('Your account changed. Reopen Contacts to check the deletion.');
        dispatch({ type: CONTACT_DELETED, contactId });
        // Replace the invalidated list snapshot with a read begun after confirmation.
        void loadContacts()(dispatch, getState);
    };
}
/**
 * Toggle favorite status.
 */
export function toggleFavorite(contactId, currentFavorite) {
    return async (dispatch, getState) => {
        if (currentFavorite !== 0 && currentFavorite !== 1)
            throw new Error('Refresh Contacts before changing favorites.');
        const scope = contactDetailScope(), state = getState()['features/contacts'];
        if (state.selectedContactId === contactId && (state.isDetailLoading || state.detailError)) {
            throw new Error('Refresh this contact before changing favorites.');
        }
        const contact = await contactsAPI.setFavorite(contactId, currentFavorite === 0);
        if (!sameDetailScope(scope))
            throw new Error('Your account changed. Reopen Contacts to check the favorite.');
        dispatch({ type: CONTACT_FAVORITE_CONFIRMED, contact });
    };
}
/**
 * Add a note to a contact.
 */
function noteContext(contactId, getState) {
    const state = getState()['features/contacts'];
    if (state.contactDetail?.contact.id !== contactId || !state.detailViewId || state.detailError || state.isDetailLoading) {
        throw new Error('Refresh this contact before changing its notes.');
    }
    if (contactId.startsWith('platform-'))
        throw new Error('Save this directory contact before adding notes.');
    const scope = contactDetailScope();
    if (state.detailScope !== scope)
        throw new Error('Your account changed. Reopen Contacts before changing notes.');
    return { contactId, viewId: state.detailViewId, scope };
}
export function addNote(contactId, content) {
    return async (dispatch, getState) => {
        const context = noteContext(contactId, getState);
        const result = await contactsAPI.addNote(contactId, content);
        if (!sameDetailScope(context.scope))
            throw new Error('Your account changed. Reopen Contacts to check the save.');
        dispatch({ type: CONTACT_NOTE_ADDED, ...context, note: result.note });
    };
}
export function updateNote(contactId, noteId, content) {
    return async (dispatch, getState) => {
        const context = noteContext(contactId, getState);
        await contactsAPI.updateNote(contactId, noteId, content);
        if (!sameDetailScope(context.scope))
            throw new Error('Your account changed. Reopen Contacts to check the save.');
        dispatch({ type: CONTACT_NOTE_UPDATED, ...context, notePatch: { id: noteId, content } });
    };
}
export function deleteNote(contactId, noteId) {
    return async (dispatch, getState) => {
        const context = noteContext(contactId, getState);
        await contactsAPI.deleteNote(contactId, noteId);
        if (!sameDetailScope(context.scope))
            throw new Error('Your account changed. Reopen Contacts to check the change.');
        dispatch({ type: CONTACT_NOTE_DELETED, ...context, noteId });
    };
}
/**
 * Load contact groups.
 */
export function loadGroups() {
    return async (dispatch) => {
        try {
            const result = await contactsAPI.listGroups();
            dispatch({ type: CONTACT_GROUPS_LOADED, groups: result.groups });
        }
        catch {
            // Groups are non-critical
        }
    };
}
/**
 * Create a contact group.
 */
export function addGroup(data) {
    return async (dispatch) => {
        try {
            const result = await contactsAPI.createGroup(data);
            if (result.group) {
                dispatch({ type: CONTACT_GROUP_ADDED, group: result.group });
            }
            else {
                dispatch(loadGroups());
            }
        }
        catch (error) {
            dispatch({ type: CONTACTS_ERROR, error: error.message });
        }
    };
}
/**
 * Delete a contact group.
 */
export function deleteGroup(groupId) {
    return async (dispatch) => {
        try {
            await contactsAPI.deleteGroup(groupId);
            dispatch({ type: CONTACT_GROUP_DELETED, groupId });
        }
        catch (error) {
            dispatch({ type: CONTACTS_ERROR, error: error.message });
        }
    };
}
/**
 * Load blocked contacts.
 */
export function loadBlocked() {
    return async (dispatch) => {
        try {
            const result = await contactsAPI.listBlocked();
            dispatch({ type: CONTACT_BLOCKED_LOADED, blocked: result.blocked });
        }
        catch {
            // Non-critical
        }
    };
}
/**
 * Sync contacts from server (delta sync).
 */
export function syncContacts() {
    return async (dispatch, getState) => {
        const state = getState();
        const syncToken = state['features/contacts']?.syncToken;
        try {
            const result = await contactsAPI.sync(syncToken);
            dispatch({
                type: CONTACTS_SYNC_RECEIVED,
                changes: result.changes,
                serverTimestamp: result.serverTimestamp
            });
            dispatch({ type: CONTACTS_SYNC_TOKEN_UPDATED, token: result.serverTimestamp });
        }
        catch {
            // Fallback: full reload
            dispatch(loadContacts());
        }
    };
}
/**
 * Import contacts from file data.
 */
export function importContacts(contacts) {
    return async (dispatch) => {
        try {
            const result = await contactsAPI.import(contacts);
            dispatch(loadContacts());
            dispatch(loadGroups());
            return result;
        }
        catch (error) {
            dispatch({ type: CONTACTS_ERROR, error: error.message });
            throw error;
        }
    };
}
// UI actions
export function setSearch(search) {
    return { type: CONTACTS_SET_SEARCH, search };
}
export function setTab(tab) {
    return { type: CONTACTS_SET_TAB, tab };
}
export function setGroupFilter(groupId) {
    return { type: CONTACTS_SET_GROUP_FILTER, groupIdFilter: groupId };
}
export function clearError() {
    return { type: CONTACTS_CLEAR_ERROR };
}
